import { Typography } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useContext } from "react";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NO_DRAG_SELECTOR } from "../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import DialogHeader from "../../../common/components/functional/dialogs/DialogHeader";
import OverlaySpinner from "../../../common/components/functional/overlaySpinner/OverlaySpinner";
import isEmpty from "../../../common/helpers/isEmpty";
import { CustomerContract } from "../customerAPI";
import { updateContract } from "../customerContractsSlice";
import { ActionButtonsContainer, ModalContainer } from "../styles";
import ContractPartiesSection from "./form/ContractPartiesSection";
import CustomerContactsSection from "./form/CustomerContactsSection";
import EezyResponsibleSection from "./form/EezyResponsibleSection";
import InvoiceReferencesSection from "./form/InvoiceReferencesSection";
import InvoicingDetailsSection from "./form/InvoicingDetailsSection";
import SalaryAndInvoicingPeriodsSection from "./form/SalaryAndInvoicingPeriodsSection";
import {
  handleContractDataForApi,
  handleCustomerOrganizationsDataForForm,
  handleCustomerUserDataForForm,
  handleExpertUserDataForForm,
  handleFactoringSalesAccountDataForForm,
  handleInvoicesSeparation,
  handleOrderQueueDataForForm,
} from "./helpers/contractHelpers";
import { StyledFormGroup } from "./styles";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import FactoringInvoicingSection from "./form/FactoringInvoicingSection";
import { YesNoOptions } from "./constants";
import { useContractDataContext } from "./ContractDataProvider";
import { ContractFormValues } from "./types";
import { InvoiceDeliveryDateSection } from "./form/InvoiceDeliveryDateSection";
import { InvoiceLanguageSection } from "./form/InvoiceLanguageSection";

interface Props {
  contractData: CustomerContract;
  closeDialog: () => void;
}

const ContractForm = ({ contractData, closeDialog }: Props) => {
  const { t } = useTranslation(["customers", "schedulingPeriods", "common"]);
  const dispatch = useAppDispatch();

  const { canEditContract } = useContext(AccessRightContext);

  const {
    allCustomerOrganizations,
    representativeCustomerOrganization,
    subCustomerOrganizations,
    expertUserOptions,
    customerUsers,
    orderQueues,
    salesAccountsOptions,
  } = useContractDataContext();

  const contracts = useAppSelector((state) => state.customerContracts);
  const isLoading = contracts.status === "pending";

  const defaultValues = {
    customerCompanyName: representativeCustomerOrganization?.name ?? "",
    eezyCompanyName: contractData.eezyCompany.name,
    validFromDate: !isEmpty(contractData.validFrom)
      ? new Date(contractData.validFrom as string)
      : null,
    customerOrgId: handleCustomerOrganizationsDataForForm({
      corgIdList: contractData.customerOrgId,
      customerOrganizations: subCustomerOrganizations,
    }),
    salesPerson: handleExpertUserDataForForm({
      userId: contractData.salesPersonId,
      userName: contractData.salesPersonName,
      expertUserOptions,
    }),
    orderQueue: handleOrderQueueDataForForm({
      orderQueueId: contractData.orderQueueId,
      orderQueues,
    }),
    contractResponsiblePerson: handleCustomerUserDataForForm({
      userId: contractData.contractResponsiblePerson?.id,
      customerUsers,
    }),
    invoicingResponsiblePerson: handleCustomerUserDataForForm({
      userId: contractData.invoicingResponsiblePerson?.id,
      customerUsers,
    }),
    paymentTermDays: !isEmpty(contractData.paymentTermDays, true)
      ? contractData.paymentTermDays?.toString()
      : "",
    invoicesSeparation: handleInvoicesSeparation(
      contractData.invoicesByCostCenter,
      contractData.invoicesByWorkLocation,
    ),
    invoiceFormat: contractData.invoiceFormat || "",
    invoiceBreakdown: contractData.invoiceBreakdown || [],
    invoiceDeliveryDateEnabled: !!contractData.invoiceDeliveryDate
      ? YesNoOptions.Yes
      : YesNoOptions.No,
    invoiceLanguage: contractData.invoiceLanguage,
    salesAccountCode: contractData?.salesAccount?.code || "",
    factoringEnabled: !!contractData.factoring
      ? YesNoOptions.Yes
      : YesNoOptions.No,
    factoringContractId: contractData.factoring?.factoringContractId || "",
    factoringSalesAccountCode: handleFactoringSalesAccountDataForForm({
      factoringSalesAccountCode: contractData.factoring?.salesAccount,
      salesAccountsOptions,
    }),
    factoringCustomerOrgIds: handleCustomerOrganizationsDataForForm({
      corgIdList: contractData.factoring?.customerOrgIds,
      customerOrganizations: allCustomerOrganizations,
    }),
    orderIdentifier: contractData?.invoiceReferences?.orderIdentifier || "",
    agreementIdentifier:
      contractData?.invoiceReferences?.agreementIdentifier || "",
    sellerReferenceIdentifier:
      contractData?.invoiceReferences?.sellerReferenceIdentifier || "",
    buyerReferenceIdentifier:
      contractData?.invoiceReferences?.buyerReferenceIdentifier || "",
    hourlyWorkersSchedulingClassId: contractData.schedulingClassId || "",
    monthlyWorkersSchedulingClassId:
      contractData.monthlyWorkSchedulingClassId || "",
  };

  const methods = useForm<ContractFormValues>({
    defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    formState: { isDirty, errors, isSubmitting },
  } = methods;

  const onSubmit = (formData: any) => {
    const updatedContract = handleContractDataForApi(contractData, formData);
    dispatch(updateContract(updatedContract)).then((action) => {
      if (isFulfilled(action)) closeDialog();
    });
  };

  const onSave = () => {
    handleSubmit(onSubmit)();
  };

  const onCancel = () => {
    reset(defaultValues);
  };

  const handleClose = () => {
    onCancel();
    closeDialog();
  };

  const TitleComponent = (
    <Typography variant="header" fontSize="23px" fontWeight="600">
      {contractData.name}
    </Typography>
  );

  const saveDisabled =
    !isDirty || !isEmpty(errors) || isSubmitting || isLoading;

  return (
    <ModalContainer>
      <DialogHeader title={TitleComponent} onClose={handleClose} />
      <FormProvider {...methods}>
        <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
          <StyledFormGroup className={NO_DRAG_SELECTOR}>
            <OverlaySpinner isLoading={isLoading} />
            <ContractPartiesSection />
            <EezyResponsibleSection />
            <CustomerContactsSection />
            <InvoicingDetailsSection />
            <FactoringInvoicingSection />
            <InvoiceDeliveryDateSection />
            <InvoiceLanguageSection />
            <InvoiceReferencesSection />
            <SalaryAndInvoicingPeriodsSection />
          </StyledFormGroup>
        </UnsavedChangesProvider>
      </FormProvider>
      <ActionButtonsContainer marginTop="1rem">
        <CustomButton
          accessRightChecks={[canEditContract(contractData.eezyCompany.id)]}
          variant="contained"
          sx={{ borderRadius: "15px", height: "30px" }}
          onClick={onSave}
          disabled={saveDisabled}
        >
          {t("common:save")}
        </CustomButton>
      </ActionButtonsContainer>
    </ModalContainer>
  );
};

export default ContractForm;
